import React, { useRef } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import cx from 'classnames'

import { Account } from '@driftt/api-types'
import { Link } from '@driftt/dds'
import { Button, Div, H1, H5, ToolTip } from '@driftt/tide-core'

import AccountSyncFromSalesforcePanel from 'components/AccountSyncFromSalesforcePanel'
import { AccountActivityAccountAvatar } from 'components/Avatars'
import Breadcrumb from 'components/Breadcrumb'
import { LocationState } from 'hooks/useAccountCompanyTitle'
import { CompanyData } from 'modules/accountActivity/domain'
import { EngagementScore } from 'pages/AccountsPage'
import { getEnabledIntegrations } from 'selectors'
import { segmentTrack, TrackType } from 'utils/tracking'
import { prefixUrlWithHttp } from 'utils/urlUtils'

import { ReactComponent as LightningBolt } from '../../../svgs/lightning-bolt-large.svg'
import { ReactComponent as LinkedInIcon } from '../../../svgs/linkedin.svg'
import { ReactComponent as SalesforceIcon } from '../../../svgs/salesforce.svg'
import { ReactComponent as TwitterIcon } from '../../../svgs/twitter.svg'

import styles from './styles.module.css'

const LINKEDIN_URL = 'https://www.linkedin.com/'
const TWITTER_URL = 'https://www.twitter.com/'

interface Props {
  activeTab: string
  tabNames: string[]
  onTabSelected: (selectedTab: string) => void
  account: Account | undefined
  company: CompanyData | undefined
  explainData: any | undefined
  salesforceData: any | undefined
}

const AccountDetailsHeader = (props: Props) => {
  const location = useLocation<LocationState>()
  const { account, company, explainData, salesforceData } = props
  const salesforceAccount = salesforceData?.accounts ? salesforceData?.accounts[0] : {}
  const enabledIntegrations = useSelector(getEnabledIntegrations)
  const hasSalesforceConnected = enabledIntegrations['salesforce']
  const engagementRef = useRef<HTMLDivElement>(null)

  const navigation = {
    title: location.state?.navigatedFrom,
    link: location.state?.navigatedFromUrl
  }

  const companyDomain = account?.domain || company?.domain

  const handleGoToDomain = () => {
    segmentTrack(TrackType.ACCOUNT_DETAILS_VIEW_DOMAIN_CLICK)
    window.open(prefixUrlWithHttp(company?.url || companyDomain))
  }

  return (
    <Div className={styles.headerContainer}>
      <Div className={styles.headerSplash}>
        <section className={styles.navigationContainer}>
          <Breadcrumb to={{ title: account?.name ?? 'Account details' }} from={navigation} />
        </section>
        <LightningBolt />
      </Div>
      <Div className={styles.accountInfoContainer}>
        <Div className={styles.leftSide}>
          <AccountActivityAccountAvatar className={styles.avatar} imageUrl={company?.logo} />
          <Div className={styles.accountInfo}>
            <Div className={styles.headline}>
              <H1 className={styles.accountName}>
                {account?.name || company?.name || 'Unknown Account'}
              </H1>
              <Div className={styles.engagementScoreFlames} ref={engagementRef}>
                <EngagementScore
                  boundaryRef={engagementRef.current}
                  accountActivity={explainData}
                  placement="bottom-end"
                  showMore={false}
                />
              </Div>
            </Div>
            {companyDomain && (
              <Link
                href={prefixUrlWithHttp(companyDomain)}
                onClick={() => segmentTrack(TrackType.ACCOUNT_DETAILS_VIEW_DOMAIN_CLICK)}
                rel="noopener noreferrer"
                size="medium"
                target="_blank"
                variant="inline"
                className={styles.accountDomain}>
                @{companyDomain}
              </Link>
            )}
            <Div className={styles.iconActions}>
              {company?.linkedinHandle && (
                <div className={styles.iconAction}>
                  <ToolTip flow="up" component="LinkedIn" tethered>
                    <a
                      href={`${LINKEDIN_URL}${company?.linkedinHandle}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.icon}>
                      <LinkedInIcon />
                    </a>
                  </ToolTip>
                </div>
              )}
              {company?.twitterHandle && (
                <div className={styles.iconAction}>
                  <ToolTip flow="up" component="Twitter" tethered>
                    <a
                      href={`${TWITTER_URL}${company?.twitterHandle}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.icon}>
                      <TwitterIcon />
                    </a>
                  </ToolTip>
                </div>
              )}
              {salesforceData?.baseUrl && salesforceAccount && (
                <div className={styles.iconAction}>
                  <ToolTip flow="up" component="Salesforce" tethered>
                    <a
                      href={`${props.salesforceData.baseUrl}lightning/r/Account/${salesforceAccount.Id}/view`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.icon}>
                      <SalesforceIcon />
                    </a>
                  </ToolTip>
                </div>
              )}
            </Div>
          </Div>
        </Div>
        <Div className={styles.rightSide}>
          {hasSalesforceConnected && <AccountSyncFromSalesforcePanel />}
        </Div>
      </Div>
      <Div className={styles.tabsAndActions}>
        <Div className={styles.tabs}>
          {props.tabNames.map(tabName => (
            <H5
              key={tabName}
              className={cx(styles.tab, { [styles.activeTab]: props.activeTab === tabName })}
              onClick={() => props.onTabSelected(tabName)}>
              {tabName}
            </H5>
          ))}
        </Div>
        <Div className={styles.actionButtons}>
          {(company?.domain || account?.domain) && (
            <Button onClick={handleGoToDomain} className={styles.actionButton} type="tertiary">
              View {company?.domain || account?.domain}
            </Button>
          )}
        </Div>
      </Div>
    </Div>
  )
}

export default AccountDetailsHeader
